'use client'
import Image from 'next/image'

import { clsx } from 'clsx'

import { CSSProperties, FC, useState } from 'react'

import { ExpressCourse } from '@/app/graphql/generated'
import { AnimationMoveIconComponent } from '@/app/shared/components/animation/move-icon'
import { CourseInfoComponent } from '@/app/shared/components/modals/course-info'
import { ImageLetsGo } from '@/app/shared/images'
import { IClassName } from '@/app/shared/interfaces/common.interface'
import { getTypeBtn } from '@/app/shared/services/btn-type'
import useModal from '@/app/shared/store/modals.store'
import { ButtonComponent } from '@/app/shared/ui/button'
import { t } from '@/core/lib/locale/i18n'

// interface
interface ICard extends IClassName {
  data: ExpressCourse | null
}

// component
const CardComponent: FC<Readonly<ICard>> = ({ data, className }) => {
  const currentColor = data?.color?.hex_value || '#000000'

  const open = useModal((state) => state.open)

  const borderColor = `!border-${getTypeBtn(data?.color?.id)}`

  const [isHovered, setIsHovered] = useState(false)

  const handleOpenModal = () => {
    open({
      component: CourseInfoComponent,
      props: {
        blockStyle: {
          color: currentColor,
          borderColor: currentColor,
        },
        classNameColor: getTypeBtn(data?.color?.id),
        data: data,
      },
      additional: {
        size: '5xl',
        className: 'border-success border-2',
        classNames: {
          base: `!overflow-hidden ${borderColor} hello`,
          body: 'p-[60px_30px_30px_30px] border-warning',
          closeButton: `flex p-1 items-center justify-center hover:bg-${getTypeBtn(data?.color?.id)} border-2 border-black bg-${getTypeBtn(data?.color?.id)} w-[34px] h-[34px] [&>svg]:w-full [&>svg]:h-full [&>svg]:text-white [&>svg]:stroke-3 active:bg-${getTypeBtn(data?.color?.id)} top-[24px] right-[24px]`,
        },
      },
    })
  }

  // return
  return (
    <div
      className={clsx(
        'flex flex-col rounded-large border-2 bg-white p-[40px_30px_30px_30px]',
        className,
      )}
      style={{
        color: currentColor,
        borderColor: currentColor,
      }}
    >
      <div className={'mb-5 flex justify-between gap-1 desktop:mb-[72px]'}>
        <h2
          className={'hyphens-auto text-[32px] font-extrabold desktop:text-[38px]'}
          style={
            {
              'word-break': 'break-word;',
            } as CSSProperties
          }
        >
          {data?.title}
        </h2>
        <AnimationMoveIconComponent
          className={'h-[62px] w-[90px] flex-shrink-0'}
          valueMoving={[30, 50, 70][Math.floor(Math.random() * 4)]}
        >
          <Image
            src={data?.sticker ?? ImageLetsGo}
            alt={'sticker'}
            width={90}
            height={90}
            className={'h-[90px] w-[90px] object-contain'}
            sizes={'(max-width:1028px):71px, (max-width:1600px):78px, 88px'}
          />
        </AnimationMoveIconComponent>
      </div>
      <div className={'mb-5 mt-auto text-black'}>{data?.description}</div>
      <ButtonComponent
        style={{
          backgroundColor: isHovered ? 'white' : currentColor,
          color: isHovered ? currentColor : 'white',
          border: `2px solid ${currentColor}`,
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        size={'md'}
        icon={'text-primary'}
        customColor={!isHovered ? 'white' : currentColor}
        iconColor={isHovered ? 'white' : currentColor}
        onClick={handleOpenModal}
      >
        {t.more()}
      </ButtonComponent>
    </div>
  )
}

export default CardComponent
